import React from 'react';
import type { ScorecardRow } from '@troia-v3/types';
import { Avatar, AvatarFallback, AvatarImage, CustomizeMenu } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import { DashCard, formatBRL, formatPct } from '@/shared/components/ui';

import {
  DEFAULT_SCORECARD_COLUMNS,
  SCORECARD_COLUMN_IDS,
  SCORECARD_COLUMNS,
  type ScorecardColumnId,
} from '../config/scorecardColumns';

interface ScorecardTableProps {
  rows: ScorecardRow[];
  columns: ScorecardColumnId[];
  onColumnsChange: (columns: ScorecardColumnId[]) => void;
}

const TH_CLASS = 'py-2 px-3 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap';
const TD_CLASS = 'py-2 px-3 whitespace-nowrap';

const MENU_ITEMS = SCORECARD_COLUMN_IDS.map((id) => ({ id, label: SCORECARD_COLUMNS[id].label }));

/** Colunas que o card do celular já mostra no cabeçalho — não repetem na grade. */
const MOBILE_HEADER_COLUMNS = new Set<ScorecardColumnId>(['revenue', 'deals', 'conversionRate', 'goal']);

const MetaBar: React.FC<{ goalPct: number | null }> = ({ goalPct }) => {
  if (goalPct === null) {
    return <span style={{ color: 'var(--theme-muted-text)' }}>—</span>;
  }
  return (
    <span className="inline-flex items-center gap-2 justify-end">
      <span
        className="inline-block h-1.5 w-12 rounded-full overflow-hidden align-middle"
        style={{ backgroundColor: 'var(--theme-card-border)' }}
      >
        <span
          className="block h-full"
          style={{
            width: `${Math.min(100, goalPct)}%`,
            backgroundColor: 'var(--theme-primary, #8b5cf6)',
          }}
        />
      </span>
      <strong style={{ color: 'var(--theme-card-text)' }}>{Math.round(goalPct)}%</strong>
    </span>
  );
};

export const ScorecardTable: React.FC<ScorecardTableProps> = ({ rows, columns, onColumnsChange }) => {
  const visible = columns.map((id) => SCORECARD_COLUMNS[id]);
  const mobileCells = visible.filter((c) => !MOBILE_HEADER_COLUMNS.has(c.id));
  const showGoal = columns.includes('goal');

  return (
    <DashCard
      icon="users"
      title="Time completo"
      subtitle="Vendedores com venda, lead ou captura no período"
      headerRight={
        <CustomizeMenu
          items={MENU_ITEMS}
          selected={columns}
          onChange={onColumnsChange}
          defaults={DEFAULT_SCORECARD_COLUMNS}
          label="Colunas"
          icon="columns"
          title="Colunas visíveis"
          data-testid="scorecard-columns"
        />
      }
      testId="scorecard-table"
      noPadding
    >
      {rows.length === 0 && (
        <div className="py-6 text-center text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          Nenhum vendedor com movimento no período.
        </div>
      )}

      {/* Desktop / tablet: tabela completa */}
      {rows.length > 0 && (
        <div className="hidden min-[900px]:block overflow-x-auto">
          {/* Largura mínima acompanha as colunas escolhidas: com poucas, a
              tabela não precisa rolar; com todas, rola em vez de espremer. */}
          <table className="w-full text-xs" style={{ minWidth: 260 + visible.length * 72 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                <th className={`${TH_CLASS} text-left`} style={{ color: 'var(--theme-muted-text)' }}>#</th>
                <th className={`${TH_CLASS} text-left`} style={{ color: 'var(--theme-muted-text)' }}>Vendedor</th>
                {visible.map((col) =>
                  col.id === 'revenue' ? (
                    <th
                      key={col.id}
                      className={`${TH_CLASS} text-right`}
                      style={{ color: 'var(--theme-card-text)' }}
                      data-testid={`scorecard-col-${col.id}`}
                    >
                      <span className="inline-flex items-center gap-[3px] align-middle">
                        {col.header}
                        <span style={{ color: 'var(--theme-primary, #8b5cf6)' }}>
                          <Icon name="chevron-down" size={12} />
                        </span>
                      </span>
                    </th>
                  ) : (
                    <th
                      key={col.id}
                      className={`${TH_CLASS} text-right`}
                      style={{ color: 'var(--theme-muted-text)' }}
                      data-testid={`scorecard-col-${col.id}`}
                    >
                      {col.header}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const numStyle = {
                  color: 'var(--theme-card-text)',
                  fontVariantNumeric: 'tabular-nums' as const,
                };
                return (
                  <tr
                    key={r.userId}
                    style={{ borderTop: idx > 0 ? '1px solid var(--theme-card-border)' : 'none' }}
                    data-testid={`scorecard-row-${idx}`}
                  >
                    <td className={`${TD_CLASS} text-left`} style={{ color: 'var(--theme-muted-text)' }}>{idx + 1}</td>
                    <td className={`${TD_CLASS} text-left`}>
                      <span className="flex items-center gap-2">
                        <Avatar className="w-7 h-7" isSquircle>
                          {r.avatarUrl && <AvatarImage src={r.avatarUrl} alt={r.name} isSquircle />}
                          <AvatarFallback isSquircle>{r.initials}</AvatarFallback>
                        </Avatar>
                        <span className="font-semibold" style={{ color: 'var(--theme-card-text)' }}>{r.name}</span>
                      </span>
                    </td>
                    {visible.map((col) =>
                      col.id === 'goal' ? (
                        <td key={col.id} className={`${TD_CLASS} text-right`}>
                          <MetaBar goalPct={r.goalPct} />
                        </td>
                      ) : (
                        <td
                          key={col.id}
                          className={`${TD_CLASS} text-right${col.bold ? ' font-bold' : ''}`}
                          style={numStyle}
                        >
                          {col.format?.(r)}
                        </td>
                      ),
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile: cards (sem perda de dados) */}
      {rows.length > 0 && (
        <div className="min-[900px]:hidden flex flex-col gap-3 p-4">
          {rows.map((r, idx) => {
            const cell = (k: string, v: string) => (
              <div key={k} className="flex flex-col gap-0.5 min-w-0">
                <span className="text-[10px] uppercase tracking-wider truncate" style={{ color: 'var(--theme-muted-text)' }}>{k}</span>
                <span className="text-xs font-semibold" style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}>{v}</span>
              </div>
            );
            return (
              <div
                key={r.userId}
                className="rounded-xl p-3 flex flex-col gap-3"
                style={{ border: '1px solid var(--theme-card-border)' }}
                data-testid={`scorecard-card-${idx}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-bold" style={{ color: 'var(--theme-muted-text)' }}>#{idx + 1}</span>
                  <Avatar className="w-8 h-8" isSquircle>
                    {r.avatarUrl && <AvatarImage src={r.avatarUrl} alt={r.name} isSquircle />}
                    <AvatarFallback isSquircle>{r.initials}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate" style={{ color: 'var(--theme-card-text)' }}>{r.name}</div>
                    <div className="text-[11px]" style={{ color: 'var(--theme-muted-text)' }}>
                      {r.deals} deals · conv. {formatPct(r.conversionRate)}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-base font-bold" style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}>
                      {formatBRL(r.revenue)}
                    </div>
                  </div>
                </div>
                {mobileCells.length > 0 && (
                  <div className="grid grid-cols-3 gap-x-3 gap-y-2">
                    {mobileCells.map((col) => cell(col.label, col.format?.(r) ?? ''))}
                  </div>
                )}
                {showGoal && (
                  <div className="flex items-center justify-start">
                    <MetaBar goalPct={r.goalPct} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </DashCard>
  );
};

ScorecardTable.displayName = 'ScorecardTable';
